import {Component, Inject, Input, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent, LiveResultHeadingType} from "@components";
import {
  CategoryItem,
  CategoryType,
  CompetitorFilter,
  CompetitorResults,
  Country,
  EventItem,
  FullScreenMode,
  Gender,
  Lookups,
  PagedFilter,
  Pages,
  Race,
  RaceSettings, ResultDisplayType
} from "@model";
import {CompetitorResultsService, LiveResultsService} from "@services";
import {environment} from "@environment";
import {LocalizedDatePipe} from "@pipes";
import {isPlatformBrowser, Location} from "@angular/common";

@Component({
  selector: 'app-live-results-race-details',
  templateUrl: './live-results-race-details.component.html',
  styleUrls: ['./live-results-race-details.component.scss'],
})
export class LiveResultsRaceDetailsComponent extends AbstractComponent implements OnInit {

  static readonly TIMEOUT_30 = 30000;
  static readonly TIMEOUT_10 = 10000;
  static readonly MAX_TIMEOUT = 2_147_483_647; // Max 32-bit signed integer in ms

  @Input() race: Race | undefined = undefined;
  @Input() event: EventItem | undefined = undefined;
  @Input() isFullScreen: boolean = false;
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) genders: Country[] = [];
  @Input() liveResult: CompetitorResults[] | undefined = undefined
  @Input() measurementType: number | undefined = undefined;
  @Input() categories: CategoryItem[] = [];

  private intervals: Map<"results" | "finished" | "started", NodeJS.Timeout> = new Map();


  image: string = `${environment.assetsImagesPath}/assets/img/carousel/live_results.png`;

  name: string = "";
  gender: number | undefined = undefined;
  category: number | undefined = undefined;
  overall: string | undefined = undefined;
  distance: number | undefined = undefined;

  localizedDatePipe: LocalizedDatePipe;
  fullScreenMode: FullScreenMode = FullScreenMode.FULL;
  competitorLiveResults: CompetitorResults[] = [];
  filteredLiveResults: CompetitorResults[] = [];
  isRoundRace: boolean = false;
  finishedCount: number = 0;
  startedCount: number = 0;

  liveResultMale: CompetitorResults[] | undefined = undefined;
  liveResultFemale: CompetitorResults[] | undefined = undefined;
  filterCategories: CategoryItem[] = [];
  filterCategoriesLabels: string[] = [];

  isBrowser: boolean;
  isFinished: boolean = false;
  showCategories: boolean = false;
  isCarousel: boolean = false;

  constructor(private route: ActivatedRoute, private router: Router,
              public translocoService: TranslocoService, private location: Location, private resultsService: LiveResultsService, private competitorResultsService: CompetitorResultsService,
              @Inject(PLATFORM_ID) private platformId: Object) {
    super();
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.localizedDatePipe = new LocalizedDatePipe(translocoService);
  }

  ngOnInit(): void {
    this.showCategories = !!this.race?.raceSettings?.category?.categoryType && (this.race?.raceSettings?.category?.categoryType !== CategoryType.NOT_USED);
    if (this.isBrowser) {
      this.isCarousel = this.race?.raceSettings?.resultDisplayType === ResultDisplayType.ROTATING;
      this.setFilterCategoriesAndLabels(this.categories);
      this.measurementType = this.race?.raceSettings?.measurementType;
      this.isRoundRace = (RaceSettings.isRoundRace(this.race?.raceSettings?.minimumLapDuration) || Race.isBackyardUltra(this.race?.sport));
      this.startFetchingResults();
    }
  }

  startFetchingResults() {
    // Check if the race has valid start and end times
    if (this.race?.raceStart && this.race?.raceEnd) {
      const raceStart = new Date(this.race.raceStart).getTime();
      const raceEnd = new Date(this.race.raceEnd).getTime();
      const now = Date.now();

      if (now >= raceStart) {
        // If race has started, fetch the results immediately
        this.startFetchingCompetitorsResults();
        this.startFetchingFinishedCount();
        this.startFetchingStartedCount();
        // Schedule the finalization process when the race ends + 30 seconds
        this.scheduleFinalize(raceEnd + LiveResultsRaceDetailsComponent.TIMEOUT_30 - now);
      } else {
        this.scheduleStart(raceStart - now);
        this.scheduleFinalize(raceEnd + LiveResultsRaceDetailsComponent.TIMEOUT_30 - now);
      }
    }
  }

  private startFetchingCompetitorsResults() {
    this.fetchResults();
    this.intervals.set("results", setInterval(() => this.fetchResults(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  private startFetchingFinishedCount() {
    this.fetchFinishCount();
    this.intervals.set("finished", setInterval(() => this.fetchFinishCount(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  private startFetchingStartedCount() {
    this.fetchStartedCount();
    this.intervals.set("started", setInterval(() => this.fetchStartedCount(), LiveResultsRaceDetailsComponent.TIMEOUT_10));
  }

  scheduleStart(delay: number) {
    if (delay > LiveResultsRaceDetailsComponent.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleStart(delay - LiveResultsRaceDetailsComponent.MAX_TIMEOUT), LiveResultsRaceDetailsComponent.MAX_TIMEOUT);
    } else {
      setTimeout(() => {
        this.startFetchingCompetitorsResults()
        this.startFetchingFinishedCount();
        this.startFetchingStartedCount();
      }, delay);
    }
  }

  fetchFinishCount() {
    this.resultsService.getFinishedCount(this.race?.id).subscribe(count => {
      this.finishedCount = count ?? 0;
    })
  }

  fetchStartedCount() {
    this.resultsService.getStartedCount(this.race?.id).subscribe(count => {
      this.startedCount = count ?? 0;
    })
  }

  fetchResults() {
    if (this.race?.id) {
      this.competitorResultsService.getAllCompetitorResults(this.race!.id!).subscribe(results => {
        this.competitorLiveResults = results;
        this.filterResults();
      })
    }
  }

  stopFetching() {
    this.intervals.forEach((intervalId, key) => {
      clearInterval(intervalId);
      this.intervals.delete(key);
    });
  }


  scheduleFinalize(timeAfterRaceEnd: number) {
    if (timeAfterRaceEnd > LiveResultsRaceDetailsComponent.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleFinalize(timeAfterRaceEnd - LiveResultsRaceDetailsComponent.MAX_TIMEOUT), LiveResultsRaceDetailsComponent.MAX_TIMEOUT);
    } else {
      setTimeout(() => {
        this.isFinished = true;
        this.stopFetching();
        this.fetchResults();
        this.fetchFinishCount();
        this.fetchStartedCount();
      }, timeAfterRaceEnd);
    }
  }

  setFilterCategoriesAndLabels(categories: CategoryItem[]) {
    if (categories && this.showCategories) {
      this.filterCategories = categories;
      this.filterCategoriesLabels = this.filterCategories.map((category: CategoryItem) => category.code ?? "-");
    }
  }

  handleExploreResult(liveResults: CompetitorResults[]): void {
      this.competitorLiveResults = liveResults ? [...liveResults] : [];
      this.filterResults();
  }

  sliceResults(results: CompetitorResults[]): CompetitorResults[] {
    const firstThree = results.slice(0, 3);
    const lastSeven = results.slice(-7);

    return [...firstThree, ...lastSeven.filter(item => !firstThree.includes(item))];
  }

  filterResults() {
    if (this.isFullScreen) {
      this.filteredLiveResults = this.isFinished || this.isCarousel ? [...this.competitorLiveResults] : this.sliceResults(this.competitorLiveResults);
      const femaleResults = this.competitorLiveResults.filter(result => result.sex === Gender.FEMALE);
      const maleResults = this.competitorLiveResults.filter(result => result.sex === Gender.MALE);
      this.liveResultFemale = this.isFinished || this.isCarousel ? femaleResults : this.sliceResults(femaleResults);
      this.liveResultMale = this.isFinished || this.isCarousel ? maleResults :this.sliceResults(maleResults);
    }
    else {
      this.filteredLiveResults = [...this.competitorLiveResults.filter(result => {
        const fullName = `${result.firstName ?? ""} ${result.lastName ?? ""}`.toLowerCase();
        const startNumberMatch = this.name.trim().length === 0 || result.startNumber?.toString().includes(this.name.trim().toLowerCase());
        const nameMatch = this.name.trim().length === 0 || fullName.includes(this.name.trim().toLowerCase());
        const genderMatch = this.gender === undefined || result.sex === this.gender;
        const categoryMatch = this.category === undefined || result.category === this.category;
        return (nameMatch || startNumberMatch) && genderMatch && categoryMatch;
      })]
    }
  }

  handleInput(value: string) {
    this.name = value.trim() ?? "";
    this.filterResults();
  }

  handleGenderSelection(value?: number): void {
    this.initFilterCategories(value);
    if (value === this.gender || !value) {
      this.gender = undefined;
    } else {
      this.gender = value;
    }
    this.category = undefined;
    this.filterResults();
  }

  handleCategorySelection(value?: any): void {
    if (value === this.category || !value) {
      this.category = undefined;
    } else {
      this.category = value;
    }
    this.filterResults();
  }

  initFilterCategories(value?: any) {
    if (value === Gender.MALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.MALE));
    } else if (value === Gender.FEMALE) {
      this.setFilterCategoriesAndLabels(this.categories.filter((category: CategoryItem) => category.gender === Gender.FEMALE));
    } else {
      this.setFilterCategoriesAndLabels(this.categories);
    }
  }

  createFilter() {
    return new PagedFilter<CompetitorFilter>(new Pages(), new CompetitorFilter(
      this.race?.eventId, this.race?.id, this.category ? [this.category] : undefined, this.name !== "" ? this.name : undefined, this.gender
    ))
  }

  toggleView() {
    if (this.fullScreenMode === FullScreenMode.SPLIT) {
      this.fullScreenMode = FullScreenMode.FULL;
    } else {
      this.fullScreenMode = FullScreenMode.SPLIT;
    }
  }

  protected readonly LiveResultHeadingType = LiveResultHeadingType;
  protected readonly FullScreenMode = FullScreenMode;
  protected readonly Gender = Gender;

  back(): void {
    this.location.back();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.intervals.forEach((intervalId, key) => {
      clearInterval(intervalId);
      this.intervals.delete(key);
    });
  }
}
